<template>
  <text-box :label="label" :icon="icon">
    <template v-slot:info-dialog>
      <slot name="info-dialog"></slot>
    </template>
    <div v-if="isMissing" class="d-flex justify-center align-center" style="height: 200px; font-size: 28px;">
      {{ $t('components.TextBoxMetric.notAvailable') }}
    </div>
    <v-chart v-else :option="options" :init-options="{renderer: 'canvas'}" autoresize style="width: 100%; height: 200px;" />
  </text-box>
</template>

<script>
import ECharts from 'vue-echarts'
import 'echarts/lib/chart/gauge'
import TextBox from '@/components/TextBox.vue'

const COLOR_GREEN = '#4caf50'
const COLOR_YELLOW = '#fbc02d'
const COLOR_RED = '#e53935'

export default {
  components: {
    TextBox,
    'v-chart': ECharts
  },
  props: {
    label: {
      type: String,
      required: true
    },
    value: {
      type: Number,
      required: true
    },
    inverted: {
      type: Boolean,
      default: false,
      required: false
    },
    icon: {
      type: String,
      default: 'chart-bell-curve',
      required: false
    }
  },
  computed: {
    isMissing () {
      return this.value === null || this.value === undefined || Number.isNaN(this.value)
    },
    clampedValue () {
      if (this.value < 0) return 0
      if (this.value > 100) return 100
      return this.value
    },
    colorStops () {
      // Each zone boundary is rendered as a 1%-wide white sliver inside the
      // colored band, replacing the auto split lines so the breaks land
      // exactly at the zone separators rather than at static 20/40/60/80.
      if (this.inverted) {
        // Correlation: low = poor (red), high = excellent (green)
        return [
          [0.595, COLOR_RED],
          [0.605, '#fff'],
          [0.845, COLOR_YELLOW],
          [0.855, '#fff'],
          [1, COLOR_GREEN]
        ]
      }
      // MAPE / RAE: low = excellent (green), high = poor (red)
      return [
        [0.095, COLOR_GREEN],
        [0.105, '#fff'],
        [0.245, COLOR_YELLOW],
        [0.255, '#fff'],
        [1, COLOR_RED]
      ]
    },
    options () {
      // Show labels only at the zone boundaries (plus the 0 and 100 endpoints).
      // splitNumber 20 makes ECharts emit a label callback every 5 units, which
      // includes all the boundary values for both inverted and non-inverted gauges.
      const labelValues = this.inverted ? [0, 60, 85, 100] : [0, 10, 25, 100]
      return {
        textStyle: {
          fontFamily: '"Roboto","Helvetica",sans-serif'
        },
        series: [{
          type: 'gauge',
          min: 0,
          max: 100,
          startAngle: 200,
          endAngle: -20,
          splitNumber: 20,
          radius: '95%',
          center: ['50%', '62%'],
          axisLine: {
            lineStyle: {
              width: 36,
              color: this.colorStops
            }
          },
          progress: {
            show: false
          },
          pointer: {
            length: '75%',
            width: 5,
            itemStyle: {
              color: '#424242'
            }
          },
          axisTick: {
            show: false
          },
          splitLine: {
            show: false
          },
          axisLabel: {
            distance: -38,
            color: '#616161',
            fontSize: 11,
            // rotate: 360 is visually a no-op (full revolution), but it routes
            // ECharts gauge label rendering through the rotated branch in
            // GaugeView._renderTicks, which uses align: 'center' for every
            // label. Without this, ECharts auto-aligns labels per angular
            // position (cos > 0.4 → 'right'), which silently overrides the
            // user-supplied axisLabel.align and makes the "100" label appear
            // shifted left of where the symmetric "0" sits.
            rotate: 360,
            formatter: function (value) {
              const rounded = Math.round(value)
              if (labelValues.indexOf(rounded) !== -1) {
                return String(rounded)
              }
              return ''
            }
          },
          anchor: {
            show: true,
            size: 8,
            itemStyle: {
              color: '#424242'
            }
          },
          title: {
            show: false
          },
          detail: {
            valueAnimation: true,
            formatter: function (value) {
              return Math.round(value) + '%'
            },
            fontSize: 22,
            fontWeight: 'bold',
            color: '#424242',
            offsetCenter: [0, '38%']
          },
          data: [{
            value: this.clampedValue
          }]
        }]
      }
    }
  }
}
</script>
